import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, X, Microscope } from "lucide-react";
import { Input } from "@/components/ui/input";
import GlassPill from "@/components/ui/GlassPill";
import DashboardCard from "@/components/ui/DashboardCard";
import SidebarLayout from "@/components/layout/SidebarLayout";
import { useProjectNavigation } from "@/hooks/goal-planner/use-optimized-navigation";
import { useDebouncedValue } from "@/lib/utils";
import { useWorkbookResearch } from "@/hooks/content-generator/useWorkbookResearch";
import ProjectResearchGrid from "./ProjectResearchGrid";
import ResearchLightboxUniversal from "@/components/research-generator/ResearchLightboxUniversal";
import { useToggleResearchFavorite } from "@/hooks/research-generator/library/use-toggle-research-favorite";
import DashboardButton from "@/components/ui/DashboardButton";
import UniversalMultiselectSidebar from "@/components/content-generator/editor/sidebar/UniversalMultiselectSidebar";
import { useBulkResearchImport } from "@/hooks/content-generator/useBulkResearchImport";

interface WorkbookResourcesTabProps {
  pageId: string;
  onCreateResearch?: () => void;
  autoOpenResearchId?: string | null;
  onAutoOpenComplete?: () => void;
}

export const WorkbookResourcesTab = ({ pageId, onCreateResearch, autoOpenResearchId, onAutoOpenComplete }: WorkbookResourcesTabProps) => {
  // Get goal data from pageId (same pattern as Tasks page)
  const { data: navigationData } = useProjectNavigation(pageId);
  const goal = navigationData?.goal;

  // Bulk import functionality
  const bulkImportMutation = useBulkResearchImport(pageId);

  const handleBulkImport = async (selectedResearchIds: string[]) => {
    await bulkImportMutation.mutateAsync(selectedResearchIds);
  };
  
  if (!goal) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-300">
          Goal not found for this project.
        </p>
      </div>
    );
  }

  // State for AI generation (moved to parent but kept for compatibility)
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [isReplacementGenerating, setIsReplacementGenerating] = useState(false);
  
  // Search functionality - simplified to match working tabs
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 500);
  
  // Lightbox state
  const [selectedResearchId, setSelectedResearchId] = useState<string | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Query for research directly linked to this workbook
  const { 
    data: workbookResearchData = [], 
    isLoading: isLoadingResearch 
  } = useWorkbookResearch(pageId);
  
  // Transform research data for the grid
  const researchItems = useMemo(() => {
    if (!workbookResearchData) return [];
    
    return workbookResearchData.map(item => ({
      id: item.id,
      title: item.title,
      content: item.content,
      research_type: String(item.research_type),
      created_at: item.created_at,
      is_favorited: false, // TODO: Add favoriting system if needed
      favorite_count: 0,
      research_parameters: item.research_parameters || {},
      original_prompt: item.original_prompt,
      sources: item.sources || [],
      related_questions: item.related_questions || [],
      usage: item.usage || {},
      metadata: item.metadata || {}
    }));
  }, [workbookResearchData]);
  
  // Handle toggle favorite
  const toggleFavoriteMutation = useToggleResearchFavorite();
  
  const handleToggleFavorite = async (researchId: string) => {
    try {
      // Find the current research item to get its favorited status
      const currentResearch = researchItems.find(item => item.id === researchId);
      const isFavorited = currentResearch?.is_favorited || false;
      
      await toggleFavoriteMutation.mutateAsync({
        researchId,
        isFavorited
      });
      console.log('Toggled favorite for research:', researchId);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };
  
  // Filter research based on search query
  const filteredResearch = useMemo(() => {
    if (!debouncedSearch) return researchItems;
    
    const query = debouncedSearch.toLowerCase();
    return researchItems.filter(item =>
      item.title.toLowerCase().includes(query) ||
      item.content.toLowerCase().includes(query)
    );
  }, [researchItems, debouncedSearch]);
  
  // Auto-open research lightbox when requested
  useEffect(() => {
    if (autoOpenResearchId && researchItems.length > 0) {
      // Check if the research exists in our current data
      const targetResearch = researchItems.find(item => item.id === autoOpenResearchId);
      if (targetResearch) {
        console.log('[WorkbookResourcesTab] Auto-opening lightbox for research:', autoOpenResearchId);
        handleResearchClick(autoOpenResearchId);
        if (onAutoOpenComplete) {
          onAutoOpenComplete();
        }
      }
    }
  }, [autoOpenResearchId, researchItems, onAutoOpenComplete]);

  // Research click handlers (same pattern as SuggestedResearch)
  const handleResearchClick = (researchId: string) => {
    setSelectedResearchId(researchId);
    setIsLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedResearchId(null);
  };

  // Get selected research item from already loaded data
  const selectedResearchItem = selectedResearchId 
    ? researchItems.find(item => item.id === selectedResearchId)
    : null;

  // Transform for lightbox (matching expected format)
  const lightboxResearchItem = selectedResearchItem ? {
    id: selectedResearchItem.id,
    title: selectedResearchItem.title,
    content: selectedResearchItem.content,
    researchType: selectedResearchItem.research_type,
    createdAt: selectedResearchItem.created_at,
    is_favorited: selectedResearchItem.is_favorited,
    original_prompt: selectedResearchItem.original_prompt || '',
    research_parameters: selectedResearchItem.research_parameters || {},
    sources: selectedResearchItem.sources || [],
    related_questions: selectedResearchItem.related_questions || [],
    usage: selectedResearchItem.usage || {},
    metadata: selectedResearchItem.metadata || {}
  } : null;

  return (
    <SidebarLayout sidebar={
      <div className="flex flex-col h-full gap-4">
        
        {/* Private Research Multiselect Sidebar */}
        <UniversalMultiselectSidebar 
          pageId={pageId} 
          contentType="research"
          onBulkImport={handleBulkImport}
        />
      </div>
    }>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        {/* Main Generated Research Content */}
        <DashboardCard className="p-3 md:p-6">
        <div className="space-y-6">
          {/* Header Section with Research Count and Create Research Button */}
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="flex items-center gap-2">
              <Microscope className="text-green-500 dark:text-green-400" size={18} />
              <h3 className="font-medium text-base">Shared Research</h3>
              {filteredResearch.length > 0 && (
                <GlassPill 
                  type={filteredResearch.length.toString()} 
                  variant="green" 
                  className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
                />
              )}
            </div>
            
            {/* Create Research Button - Desktop only */}
            {onCreateResearch && (
              <div className="hidden sm:block">
                <DashboardButton
                  onClick={onCreateResearch}
                  icon={Microscope}
                  size="sm"
                  className="h-8 px-3 py-1.5 text-sm"
                >
                  Create Research
                </DashboardButton>
              </div>
            )}
          </div>
          
          {/* Mobile Create Research Button - Full width below header */}
          {onCreateResearch && (
            <div className="sm:hidden mb-4">
              <DashboardButton
                onClick={onCreateResearch}
                icon={Microscope}
                size="sm"
                className="h-8 px-3 py-1.5 text-sm w-full"
              >
                Create Research
              </DashboardButton>
            </div>
          )}
          
          {/* Search feedback */}
          {debouncedSearch && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Showing results for "{debouncedSearch}"
            </p>
          )}
          
          {/* Research Grid */}
          <ProjectResearchGrid
            research={filteredResearch}
            isLoading={isLoadingResearch}
            onResearchClick={handleResearchClick}
            onToggleFavorite={handleToggleFavorite}
            onCreateResearch={onCreateResearch}
            searchQuery={debouncedSearch}
          />
        </div>
      </DashboardCard>
        
      {/* Research Lightbox */}
      {isLightboxOpen && lightboxResearchItem && (
        <ResearchLightboxUniversal
          isOpen={isLightboxOpen}
          onClose={handleCloseLightbox}
          research={lightboxResearchItem}
          researchDetails={lightboxResearchItem}
          isLoading={false}
          contentType="private_research"
          showRelatedQuestions={false}
          showFavoriteButton={false}
          onRelatedQuestionClick={(question) => {
            console.log('Related question clicked:', question);
          }}
        />
      )}
      </motion.div>
    </SidebarLayout>
  );
}; 